import { useState } from "react";
import { toast } from "sonner";
import { Trash2, KeyRound, UserPlus, Shield, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/common";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", username: "", email: "", password: "", role: "sales" };

export const StaffUsers = () => {
  const { user } = useAuth();
  const { data, reload } = useFetch("/users");
  const [form, setForm] = useState(EMPTY);

  const run = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      reload();
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (await run(() => api.post("/users", form), `Account created for ${form.name}`)) setForm(EMPTY);
  };

  const resetPw = (u) => {
    const pw = window.prompt(`New password for ${u.name} (min 8 characters)`);
    if (pw) run(() => api.put(`/users/${u.id}/password`, { password: pw }), "Password updated");
  };

  const remove = (u) => {
    if (window.confirm(`Delete the account for ${u.name}? They will no longer be able to log in.`)) run(() => api.delete(`/users/${u.id}`), "Account deleted");
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleRole = (u) => {
    const role = u.role === "admin" ? "sales" : "admin";
    if (window.confirm(`${role === "admin" ? "Promote" : "Demote"} ${u.name} to ${role}?`)) run(() => api.put(`/users/${u.id}/role`, { role }), `${u.name} is now ${role}`);
  };
  const isOwner = user.role === "owner";
  const roleCls = { owner: "bg-[#FACC15] text-[#0E1E38]", admin: "bg-[#0E1E38] text-white", sales: "bg-slate-100 text-slate-600" };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <Panel title="Staff accounts">
        <ul className="divide-y divide-slate-100" data-testid="staff-list">
          {(data || []).map((u) => (
            <li key={u.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`staff-row-${u.email || u.username}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900">{u.name} {u.id === user.id && <span className="text-xs text-slate-400">(you)</span>}</div>
                <div className="truncate text-sm text-slate-500">@{u.username}{u.email ? ` · ${u.email}` : ""}</div>
              </div>
              <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${roleCls[u.role] || roleCls.sales}`} data-testid={`staff-role-${u.email || u.username}`}>{u.role}</span>
              {isOwner && u.role !== "owner" && <Button size="sm" variant="outline" onClick={() => toggleRole(u)} data-testid={`staff-toggle-role-${u.email || u.username}`}>{u.role === "admin" ? <><ShieldOff className="h-4 w-4" /> Make sales</> : <><Shield className="h-4 w-4" /> Make admin</>}</Button>}
              <Button size="icon" variant="ghost" onClick={() => resetPw(u)} title="Reset password" data-testid={`staff-reset-${u.email || u.username}`}><KeyRound className="h-4 w-4" /></Button>
              {u.id !== user.id && u.role !== "owner" && <Button size="icon" variant="ghost" onClick={() => remove(u)} title="Delete" className="text-rose-600" data-testid={`staff-delete-${u.email || u.username}`}><Trash2 className="h-4 w-4" /></Button>}
            </li>
          ))}
        </ul>
      </Panel>
      <Panel title="Add staff member">
        <form onSubmit={create} className="space-y-3" data-testid="staff-create-form">
          <Input placeholder="Full name" value={form.name} onChange={set("name")} required data-testid="staff-name-input" />
          <Input placeholder="Username (what they type to log in)" value={form.username} onChange={set("username")} required data-testid="staff-username-input" />
          <Input placeholder="Email (optional)" type="email" value={form.email} onChange={set("email")} data-testid="staff-email-input" />
          <Input placeholder="Temporary password (min 8)" type="text" value={form.password} onChange={set("password")} required minLength={8} data-testid="staff-password-input" />
          <select value={form.role} onChange={set("role")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="staff-role-select">
            <option value="sales">Sales (bookings & calendar only)</option>
            <option value="admin">Admin (full access)</option>
          </select>
          <Button type="submit" className="w-full bg-[#0E1E38] hover:bg-[#162A4D]" data-testid="staff-create-submit"><UserPlus className="h-4 w-4" /> Create account</Button>
        </form>
      </Panel>
    </div>
  );
};
